import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { StepperPageShell } from "components/common/stepperPage";
import firebase from "firebase/compat/app";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";
import { useChartMindContext } from "context/ChartMindSession";
import { STEPS } from "components/dashboard/chartmind/ChartMindHeader";
import {
  getStepActions,
  getStepLabels,
} from "components/dashboard/chartmind/chartMindSteps";
import ChartMindHeader from "components/dashboard/chartmind/ChartMindHeader";
import ChartMindFooter from "components/dashboard/chartmind/ChartMindFooter";
import RecordingStep from "components/dashboard/chartmind/RecordingStep";
import DiagnosisStep from "components/dashboard/chartmind/DiagnosisStep";
import PlanStep from "components/dashboard/chartmind/PlanStep";
import ConfirmDiagnosisStep from "components/dashboard/chartmind/ConfirmDiagnosisStep";
import TreatmentPlanStep from "components/dashboard/chartmind/TreatmentPlanStep";
import DischargeStep from "components/dashboard/chartmind/DischargeStep";
import ChartStep from "components/dashboard/chartmind/ChartStep";
import EventsStep from "components/dashboard/chartmind/EventsStep";
import PearlsStep from "components/dashboard/chartmind/PearlsStep";
import DailyPlanNoteDialog from "components/dashboard/chartmind/DailyPlanNoteDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import IdleBlurOverlay from "components/common/IdleBlurOverlay";
import useIdleBlur from "hooks/useIdleBlur";
import {
  createSavedTranscript,
  deleteSavedTranscript,
  subscribeSavedTranscripts,
  updateSavedTranscript,
} from "services/chartMindSavedTranscriptsService";
import {
  createDischargeHandoff,
  sendDischargeInstructions,
  sendSharedRecordResult,
} from "services/healthRecordsShareService";
import { takePendingSharedRecord } from "services/sharedRecordHandoff";
import { DASHBOARD_CONTENT_HEIGHT } from "constants/layout";

// [BETA-CM0427] Temporary client-requested variant for the beta tester:
// hides the diagnostic Plan + Confirm steps, renames Treatment → "Plan",
// so the flow becomes Record → Diagnosis → Plan → Discharge → Chart.
// Flip to false (or delete every [BETA-CM0427] block) to restore the full flow.
const CM0427_MODE = true;

const ChartMindPage = () => {
  const { isGlobalAdmin, user, userData, organizationId } = useAuth();
  const isOrgAdmin = userData?.role === "admin" && Boolean(organizationId);
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();

  // Phone-width, which is as close as a browser gets to "this device is the phone". Used only
  // to withhold the "Record on your phone" offer, so a desktop window dragged narrow loses an
  // entry point and nothing else.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  // [BETA-KIJABE] Mirrors the gate in ChartMindSessionProvider — drives the footer save indicator.
  // Remove when beta ends.
  const isBetaKijabe = Boolean(userData?.betaAccess);

  // Admins reach ChartMind only to simulate encounters for prompt development (see the
  // "Simulation mode" alert below + the "Simulate a ChartMind encounter" button in the
  // Prompts manager). Treat those sessions like Kijabe beta: no auto-save, no Import
  // Shared Record. Mirrored in ChartMindSessionProvider so persistence is actually nooped.
  const isSimulationMode = isGlobalAdmin || isOrgAdmin;

  // Ephemeral, non-persisted sessions — drives the "not saved / will be discarded" UX.
  const isEphemeralSession = isBetaKijabe || isSimulationMode;

  // The session lives in ChartMindSessionProvider (above <Routes>) so it survives navigation
  // away from this page. We're a thin consumer here.
  const session = useChartMindContext();
  const { encounter } = session;
  const inpatient = encounter.inpatient;
  const eventsNote = encounter.eventsNote;
  const pearlsAvailable = session.pearls.available;
  // What the note is written from is there: a usable transcript, or on the Events note an event.
  const hasSource = eventsNote
    ? encounter.events.length > 0
    : Boolean(session.recording.transcript && session.recording.transcript.length >= 50);

  const [testEncounters, setTestEncounters] = useState([]);
  const [testEncountersLoading, setTestEncountersLoading] = useState(false);
  const [testEncountersError, setTestEncountersError] = useState("");
  const [confirmNewEncounterOpen, setConfirmNewEncounterOpen] = useState(false);
  const [confirmPhoneRecordingOpen, setConfirmPhoneRecordingOpen] = useState(false);
  // The Daily Plan item whose note dialog is open (inpatient only). The item is kept until the
  // dialog has finished closing, so its title and fields don't empty out mid-fade.
  const [dailyPlanNoteItem, setDailyPlanNoteItem] = useState(null);
  const [dailyPlanNoteOpen, setDailyPlanNoteOpen] = useState(false);
  const openDailyPlanNote = useCallback((item) => {
    setDailyPlanNoteItem(item);
    setDailyPlanNoteOpen(true);
  }, []);

  // Step telemetry: write a non-PHI event each time the user reaches a new step.
  // Works for Kijabe (no session persistence) and everyone else.
  // ephemeralEncounterKeyRef namespaces dedup for Kijabe users who do multiple encounters
  // in one tab — resets in performNewEncounter below.
  const emittedStepEventsRef = useRef(new Set());
  const ephemeralEncounterKeyRef = useRef(`eph-${Date.now()}`);

  // Seed the encounter from a record the provider just redeemed on the public
  // /share landing page. The record arrives via a RAM-only handoff (never
  // storage/history — it's PHI) and is consumed exactly once on mount.
  useEffect(() => {
    const pending = takePendingSharedRecord();
    if (pending) session.patientContext.set(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const step = session.navigation.currentStep;
    const orgId = userData?.organizationId;
    if (!step || !orgId || !user?.uid) return;

    const sessionKey = session.session.sessionId || ephemeralEncounterKeyRef.current;
    const eventKey = `${sessionKey}:${step}`;
    if (emittedStepEventsRef.current.has(eventKey)) return;
    emittedStepEventsRef.current.add(eventKey);

    const now = new Date();
    const dateKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const yearMonth = dateKey.slice(0, 7);

    const increment = firebase.firestore.FieldValue.increment(1);
    const orgRef = db.collection("organizations").doc(orgId);

    orgRef.collection("chartmind_daily").doc(dateKey)
      .set({ dateKey, yearMonth, steps: { [step]: increment } }, { merge: true })
      .catch((err) => console.error("[ChartMindPage] Failed to write daily stats:", err));

    orgRef.collection("chartmind_user_stats").doc(user.uid)
      .set({ userId: user.uid, months: { [yearMonth]: { [step]: increment } } }, { merge: true })
      .catch((err) => console.error("[ChartMindPage] Failed to write user stats:", err));
  }, [session.navigation.currentStep, session.session.sessionId, userData?.organizationId, user?.uid]);

  useEffect(() => {
    if (!isGlobalAdmin || !user?.uid) {
      setTestEncounters([]);
      setTestEncountersLoading(false);
      setTestEncountersError("");
      return undefined;
    }

    setTestEncountersLoading(true);
    setTestEncountersError("");

    const unsubscribe = subscribeSavedTranscripts(
      user.uid,
      (items) => {
        setTestEncounters(items);
        setTestEncountersLoading(false);
      },
      (error) => {
        setTestEncountersLoading(false);
        setTestEncountersError(
          error.message || "Failed to load test encounters.",
        );
      },
    );

    return () => unsubscribe?.();
  }, [isGlobalAdmin, user?.uid]);

  const handleSelectTestEncounter = useCallback(
    (encounter) => {
      if (!encounter?.body) {
        return;
      }

      session.recording.updateTranscript(encounter.body);
    },
    [session.recording],
  );

  const handleSaveTestEncounter = useCallback(
    async ({ id, title, body }) => {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      if (id) {
        await updateSavedTranscript(user.uid, id, { title, body });
        return;
      }

      await createSavedTranscript(user.uid, { title, body });
    },
    [user?.uid],
  );

  const handleDeleteTestEncounter = useCallback(
    async (encounter) => {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      if (!encounter?.id) {
        throw new Error("Encounter id is required");
      }

      await deleteSavedTranscript(user.uid, encounter.id);
    },
    [user?.uid],
  );

  // Update the URL silently after the session gets its first ID, so a refresh lands on the
  // canonical permalink. Uses replaceState to avoid React Router re-render/interruption.
  useEffect(() => {
    const hasSessionId = session.session.sessionId;
    const isOnBaseRoute = !urlSessionId;
    if (hasSessionId && isOnBaseRoute) {
      window.history.replaceState(null, "", `/dashboard/chartmind/${hasSessionId}`);
    }
  }, [session.session.sessionId, urlSessionId]);

  // URL → session reconciliation. The provider holds one session at a time; if the user
  // navigates to a different saved-session URL, swap the in-memory session to that one.
  // Tracks the previous-attempted ID so loadSession failures don't retry on every render.
  const lastAttemptedLoadRef = useRef(null);
  useEffect(() => {
    if (!urlSessionId) return;
    if (urlSessionId === session.session.sessionId) return;
    if (urlSessionId === lastAttemptedLoadRef.current) return;
    if (session.session.loading) return;
    lastAttemptedLoadRef.current = urlSessionId;
    session.session.loadSession(urlSessionId).catch((err) => {
      console.error("[ChartMindPage] Failed to load session from URL:", err);
    });
  }, [urlSessionId, session.session.sessionId, session.session.loading, session.session.loadSession]);

  // [BETA-CM0427] If the saved session was last on Plan or Confirm, bump it forward to Treatment
  // so the user doesn't land on a step the stepper no longer exposes.
  useEffect(() => {
    if (!CM0427_MODE) return;
    const step = session.navigation.currentStep;
    if (step === STEPS.DIAGNOSTIC_PLAN || step === STEPS.CONFIRM_DIAGNOSIS) {
      session.navigation.setCurrentStep(STEPS.TREATMENT);
    }
  }, [session.navigation.currentStep, session.navigation.setCurrentStep]);

  // [BETA-CM0427] Treatment's default Back goes to Confirm (hidden) — re-route it to Diagnosis.
  const handleFooterBack = useCallback(() => {
    if (
      (CM0427_MODE || inpatient) &&
      session.navigation.currentStep === STEPS.TREATMENT
    ) {
      session.navigation.setCurrentStep(
        pearlsAvailable && !eventsNote ? STEPS.PEARLS : STEPS.DIAGNOSIS,
      );
      return;
    }
    session.navigation.handleBack();
  }, [
    inpatient,
    eventsNote,
    pearlsAvailable,
    session.navigation.currentStep,
    session.navigation.setCurrentStep,
    session.navigation.handleBack,
  ]);

  // "New Encounter" — wipe the in-memory session. Confirm only when there's a transcript
  // worth losing (otherwise it's just noise). After clear, also drop the sessionId out of
  // the URL since we're starting fresh.
  const hasExistingEncounter =
    Boolean(session.recording.transcript && session.recording.transcript.trim().length > 0) ||
    (eventsNote && encounter.events.length > 0);

  /**
   * Cover the encounter once this screen has sat untouched.
   *
   * This is the surface where an idle cover actually earns its keep: unlike the phone, the
   * desktop shows the verbatim transcript, the differential and the chart note, and it is the
   * screen that sits facing an exam room while the clinician steps out.
   *
   * ⚠️ NOT WHILE CAPTURING. During a recording, on this device or the paired phone, the
   * transcript on screen is the live readout the clinician glances at from across the room
   * without touching the keyboard. Covering it there would hide the one thing proving the
   * encounter is being captured. Nothing is written down yet at that point either.
   *
   * Also off before there is an encounter at all: blurring an empty Record step is pure
   * friction protecting nothing.
   */
  const encounterIdle = useIdleBlur({
    enabled: hasExistingEncounter && !session.recording.captureActive,
  });

  // ============================================================================
  // THE PHONE AS A REMOTE
  // ============================================================================
  //
  // The clinician is holding the phone next to the patient, and the desktop is across the room.
  // Walking back to it to press Next is the same friction the microphone relay exists to remove.
  //
  // ⚠️ WHAT THIS IS NOT. It is not the phone driving the encounter: it cannot choose a
  // diagnosis, a test or a treatment, and those choices are what every Next is actually gated
  // on. So the phone gets the app's own step labels and one verb, and every decision about
  // whether that verb is allowed is made here, against this device's state, at the moment it
  // arrives. See CHARTMIND-PHONE-MIC.md for why full mirroring was rejected.

  // Same builder the footer uses, so the phone can never offer a Next the desktop would refuse.
  const stepActions = useMemo(
    () =>
      getStepActions({
        currentStep: session.navigation.currentStep,
        transcript: session.recording.transcript,
        selectedDiagnosesCount: session.diagnosis.selectedDiagnoses.length,
        selectedConfirmedDiagnosesCount:
          session.confirmDiagnosis.selectedConfirmedDiagnoses.length,
        clinicianImpressionRecorded:
          eventsNote || Boolean(session.diagnosis.clinicianImpression),
        cm0427Mode: CM0427_MODE,
        inpatient,
        eventsNote,
        eventsCount: encounter.events.length,
        pearlsAvailable,
        handlers: {
          onContinueToDiagnosis: session.navigation.handleContinueToDiagnosis,
          onContinueToPearls: session.navigation.handleContinueToPearls,
          onContinueToPlan: session.navigation.handleContinueToPlan,
          onContinueToConfirm: session.navigation.handleContinueToConfirm,
          onContinueToTreatment: session.navigation.handleContinueToTreatment,
          onContinueToDischarge: session.navigation.handleContinueToDischarge,
          onGenerateChart: session.navigation.handleContinueToChart,
          onSkipToChart: session.navigation.handleSkipToChart,
        },
      }),
    [
      inpatient,
      eventsNote,
      encounter.events.length,
      pearlsAvailable,
      session.navigation,
      session.recording.transcript,
      session.diagnosis.selectedDiagnoses.length,
      session.diagnosis.clinicianImpression,
      session.confirmDiagnosis.selectedConfirmedDiagnoses.length,
    ],
  );

  // Pearls turned out not to be set up on this environment while the clinician was on that
  // step: carry on with the same Next it would have pressed, so the next step generates.
  useEffect(() => {
    if (!pearlsAvailable && session.navigation.currentStep === STEPS.PEARLS) stepActions.onNext?.();
  }, [pearlsAvailable, session.navigation.currentStep, stepActions]);

  const dictationTarget = session.recording.phoneDictation.target;

  const phoneRemote = useMemo(() => {
    const stepLabel =
      getStepLabels(CM0427_MODE, inpatient, eventsNote)[session.navigation.currentStep] || "";
    // ⚠️ NOT WHILE EITHER DEVICE IS CAPTURING. Advancing mid-encounter would run the
    // differential against a transcript still being spoken, and the Record step is where the
    // phone's own microphone lives — the two would be fighting over the same screen.
    // A paste still being clarified holds Next the same way a recording does.
    const capturing = session.recording.captureActive || session.recording.pasteClarifying;
    // ⚠️ AN EMPTY stepLabel IS THE "SHOW NOTHING AT ALL" SIGNAL, distinct from a label with no
    // buttons, which is "here is where I am, there is nothing to press". The phone renders the
    // difference; only this file decides it.
    const silent = {
      stepLabel: "",
      nextLabel: "",
      nextEnabled: false,
      blockedReason: "",
      skipLabel: "",
      skipEnabled: false,
      mode: "step",
      dictateLabel: "",
    };

    /**
     * ⚠️ A DICTATION OUTRANKS THE STEP REMOTE ENTIRELY, buttons included.
     *
     * While a note section's editor is open and armed for the phone, the phone's whole job is to
     * be a microphone for that section. Leaving a "Generate Note" button under it would let the
     * clinician navigate the desktop away from the dialog their own phone is dictating into.
     */
    if (dictationTarget) {
      return { ...silent, mode: "dictate", dictateLabel: dictationTarget.title || "" };
    }

    if (!stepActions.showNext) {
      // The Chart step. Nothing to advance to, but saying so is worth a line: the clinician
      // walking back from the patient wants to know the note is what is waiting for them.
      return { ...silent, stepLabel };
    }

    const nextEnabled = !stepActions.nextDisabled && !capturing;
    const skipEnabled =
      Boolean(stepActions.onSecondary) && !stepActions.secondaryDisabled && !capturing;

    /**
     * ⚠️ THE RECORD STEP NEVER SENDS A BUTTON IT WOULD REFUSE — no greyed-out anything, for any
     * reason, including a recording still in progress. The asymmetry with every later step is
     * the point rather than an inconsistency.
     *
     * Later on, a blocked Next names something the clinician must go and do at the desktop
     * ("please confirm diagnoses on your computer"), and naming it is the entire value of
     * putting the button on the phone. On Record the only blockers are "you have not recorded
     * yet" and "you are recording right now" — and the phone is ALREADY showing a microphone the
     * size of a thumb, mid-recording or not. A dead button captioned with the state of the
     * screen's own primary control is noise.
     *
     * So the buttons arrive only once the round has been spoken, stopped, transcribed and landed
     * here as usable transcript, and they arrive already pressable.
     */
    const onRecordStep = session.navigation.currentStep === STEPS.RECORD;
    if (onRecordStep && !nextEnabled && !skipEnabled) return silent;

    return {
      stepLabel,
      nextLabel: !nextEnabled && onRecordStep ? "" : stepActions.nextLabel || "",
      nextEnabled,
      blockedReason: session.recording.captureActive
        ? "Please stop the recording first."
        : session.recording.pasteClarifying
          ? "Please wait a moment."
          : stepActions.nextBlockedReason || "",
      // Only the Record step offers one, and it is the same jump the desktop's secondary button
      // makes. Absent elsewhere, which is what keeps it off the phone on every other step.
      skipLabel: !skipEnabled && onRecordStep ? "" : stepActions.secondaryLabel || "",
      skipEnabled,
    };
  }, [
    stepActions,
    inpatient,
    eventsNote,
    session.navigation.currentStep,
    session.recording.captureActive,
    session.recording.pasteClarifying,
    dictationTarget,
  ]);

  const {
    pushRemoteState,
    pendingCommand: phoneCommand,
    acknowledgeCommand: acknowledgePhoneCommand,
  } = session.recording.phone;

  useEffect(() => {
    pushRemoteState(phoneRemote);
  }, [pushRemoteState, phoneRemote]);

  useEffect(() => {
    if (!phoneCommand) return;
    // Acknowledge before acting, so a re-render mid-navigation cannot run it twice.
    acknowledgePhoneCommand(phoneCommand.id);
    // ⚠️ RE-CHECK THE GATE HERE, for every verb. The phone's copy of "can I press this" is
    // always at least a snapshot old — it could have been pressed as the last diagnosis was
    // being deselected on this screen. The desktop's state at the moment the press lands is the
    // only authority, and an ignored press is a designed outcome rather than a failure.
    if (phoneCommand.verb === "next") {
      if (phoneRemote.nextEnabled) stepActions.onNext?.();
      return;
    }
    if (phoneCommand.verb === "skip") {
      if (phoneRemote.skipEnabled) stepActions.onSecondary?.();
    }
  }, [phoneCommand, acknowledgePhoneCommand, phoneRemote, stepActions]);

  const performNewEncounter = useCallback(() => {
    ephemeralEncounterKeyRef.current = `eph-${Date.now()}`;
    session.session.clearSession();
    session.clearPeerViewMessages();
    if (urlSessionId) {
      window.history.replaceState(null, "", "/dashboard/chartmind");
    }
  }, [session, urlSessionId]);

  // Recording on the phone starts the encounter over, so it is New Encounter with a different
  // entry point and it confirms the same way when there is something to lose.
  const handleRecordOnPhone = useCallback(() => {
    if (hasExistingEncounter) {
      setConfirmPhoneRecordingOpen(true);
      return;
    }
    session.recording.phone.start();
  }, [hasExistingEncounter, session.recording.phone]);

  /**
   * ⚠️ THE WHOLE ENCOUNTER GOES, NOT JUST THE TRANSCRIPT.
   *
   * This used to start the pairing and leave everything else standing, on the reasoning that
   * the phone's first flush would overwrite the transcript anyway. It does — but the clarifying
   * questions, the contradictions, the differential and the chart note were all derived from
   * the encounter being replaced, and they sat there through the next one looking like they
   * belonged to it. Clarifying questions were the visible half: the previous patient's
   * questions were still on screen while the new recording started.
   */
  const handleConfirmPhoneRecording = useCallback(() => {
    setConfirmPhoneRecordingOpen(false);
    performNewEncounter();
    session.recording.phone.start();
  }, [performNewEncounter, session.recording.phone]);

  const handleNewEncounterClick = useCallback(() => {
    if (hasExistingEncounter) {
      setConfirmNewEncounterOpen(true);
      return;
    }
    performNewEncounter();
  }, [hasExistingEncounter, performNewEncounter]);

  const handleConfirmNewEncounter = useCallback(() => {
    performNewEncounter();
    setConfirmNewEncounterOpen(false);
  }, [performNewEncounter]);

  // Reverse-share: send the finalized discharge instructions to the patient. With an
  // imported record the patient's uid comes from patientContext (set at import time) and
  // the send is direct; without one, DischargeStep shows a one-time QR the patient's own
  // phone claims (createDischargeHandoff).
  const importedPatient = session.patientContext.value;

  // What the patient receives, whichever way it reaches them (direct, QR or print).
  // Intentionally omits the diagnosis: what we hold is the clinician-selected (often
  // provisional, possibly multi-item) differential, which would alarm or confuse a
  // patient. The patient-facing value is the actionable guidance.
  const getPatientDischarge = useCallback(() => {
    const cmr = session.discharge.getForCMR();
    const includedIds = new Set(cmr.includedInstructionIds);
    const instructions = (cmr.instructions || [])
      .filter((inst) => includedIds.has(inst.id))
      .map(({ title, category, prose, medicationName }) => ({
        title,
        category,
        prose,
        ...(medicationName ? { medicationName } : {}),
      }));
    return { instructions, date: cmr.date };
  }, [session.discharge]);

  const handleSendDischarge = useCallback(async () => {
    const patient = session.patientContext.value;
    if (!patient?.patientUserId) {
      throw new Error("No imported patient to send to.");
    }
    const result = await sendDischargeInstructions({
      patientUserId: patient.patientUserId,
      discharge: getPatientDischarge(),
    });
    return { emailed: Boolean(result?.emailed) };
  }, [session.patientContext, getPatientDischarge]);

  const handleCreateDischargeHandoff = useCallback(
    () => createDischargeHandoff({ discharge: getPatientDischarge() }),
    [getPatientDischarge],
  );

  // A patient a partner system pushed in through the partner API: the finished visit can go
  // back to it.
  const partnerImport = importedPatient?.partnerImport || null;

  const handleSendToPartner = useCallback(async () => {
    if (!partnerImport) return;
    const sections = session.chart.template?.sections || [];
    const note = sections
      .map(({ key, title }) => ({ title, text: session.chart.noteSections?.[key] || "" }))
      .filter((s) => s.text.trim());
    const treatments = [
      ...(session.treatment.planData?.treatments || []),
      ...(session.treatment.customTreatments || []),
    ]
      .filter(
        (t) =>
          session.treatment.selectedTreatments.has(t.id) &&
          !session.treatment.disabledTreatmentIds.has(t.id),
      )
      .map(({ name, category, route }) => ({ name, category, route }));
    await sendSharedRecordResult({
      importId: partnerImport.id,
      note,
      diagnoses: session.diagnosis.selectedDiagnoses.map((d) => ({
        condition: d.condition || d.name,
      })),
      treatments,
      dischargeInstructions: getPatientDischarge().instructions.map(({ title, prose }) => ({
        title,
        text: prose,
      })),
    });
  }, [partnerImport, session.chart, session.treatment, session.diagnosis, getPatientDischarge]);

  if (session.session.loading) {
    return (
      <Box
        sx={{
          height: DASHBOARD_CONTENT_HEIGHT,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={48} />
          <Box sx={{ mt: 2, color: "text.secondary" }}>Loading session...</Box>
        </Box>
      </Box>
    );
  }

  if (urlSessionId && session.session.error) {
    return (
      <Box
        sx={{
          height: DASHBOARD_CONTENT_HEIGHT,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          px: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {session.session.error}
        </Alert>
      </Box>
    );
  }

  // The Events note always has a footer, so the Event step shows its Next (held until there is an
  // event) from the start.
  const footer =
    hasSource || eventsNote ? (
      <ChartMindFooter
        currentStep={session.navigation.currentStep}
        transcript={session.recording.transcript}
        selectedDiagnosesCount={session.diagnosis.selectedDiagnoses.length}
        selectedConfirmedDiagnosesCount={
          session.confirmDiagnosis.selectedConfirmedDiagnoses.length
        }
        onContinueToDiagnosis={session.navigation.handleContinueToDiagnosis}
        onContinueToPearls={session.navigation.handleContinueToPearls}
        onContinueToPlan={session.navigation.handleContinueToPlan}
        onContinueToConfirm={session.navigation.handleContinueToConfirm}
        onContinueToTreatment={session.navigation.handleContinueToTreatment}
        onContinueToDischarge={session.navigation.handleContinueToDischarge}
        onGenerateChart={session.navigation.handleContinueToChart}
        onSkipToChart={session.navigation.handleSkipToChart}
        onBack={handleFooterBack}
        saving={session.session.saving}
        lastSavedAt={session.session.lastSavedAt}
        hasUnsavedChanges={session.session.hasUnsavedChanges}
        disableSave={isEphemeralSession}
        cm0427Mode={CM0427_MODE}
        inpatient={inpatient}
        eventsNote={eventsNote}
        eventsCount={encounter.events.length}
        pearlsAvailable={pearlsAvailable}
        nextLoading={session.recording.pasteClarifying}
      />
    ) : null;

  return (
    <StepperPageShell
      footer={footer}
      maxWidth={700}
      header={
        <ChartMindHeader
          currentStep={session.navigation.currentStep}
          setCurrentStep={session.navigation.setCurrentStep}
          isRecording={session.recording.isRecording}
          recordingTime={session.recording.recordingTime}
          ddxLoading={session.diagnosis.loading}
          hasDiagnoses={session.diagnosis.hasDiagnoses}
          diagnosticPlanLoading={session.diagnosticPlan.loading}
          hasDiagnosticPlan={session.diagnosticPlan.hasTests}
          treatmentPlanLoading={session.treatment.loading}
          hasTreatmentPlan={session.treatment.hasTreatments}
          chartLoading={session.chart.loading}
          hasNote={session.chart.hasNote}
          hasTranscript={hasSource}
          hasSelectedDiagnosis={session.diagnosis.selectedDiagnoses.length > 0}
          visitedSteps={session.navigation.visitedSteps}
          onNewEncounter={handleNewEncounterClick}
          hasEncounterData={hasExistingEncounter}
          cm0427Mode={CM0427_MODE}
          inpatient={inpatient}
          eventsNote={eventsNote}
          pearlsAvailable={pearlsAvailable}
        />
      }
    >
      {/* Column width comes from the shell's maxWidth so ChartMind and VisitStart
          are literally the same number in the same place. This Box is layout only. */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        {/* Covers the window, not this column — see IdleBlurOverlay. It stays mounted here
            rather than at the shell because this is where the state that decides whether
            there is anything worth covering lives. */}
        <IdleBlurOverlay
          open={encounterIdle.blurred}
          onReveal={encounterIdle.reveal}
          message="This encounter is hidden for privacy while the screen was idle. Please click anywhere to show it."
        />
          {session.navigation.currentStep === STEPS.RECORD && (
            <RecordingStep
              isSupported={session.recording.isSupported}
              isRecording={session.recording.isRecording}
              isGeneratingChart={session.chart.loading}
              transcript={session.recording.transcript}
              error={session.recording.error}
              onMicClick={session.recording.onMicClick}
              onClearError={session.recording.clearError}
              onTranscriptChange={session.recording.updateTranscript}
              language={session.recording.language}
              onLanguageChange={session.recording.setLanguage}
              transcriptLabel={inpatient ? "Narrative" : "Transcript"}
              onPasteClarifyingChange={session.recording.setPasteClarifying}
              recordingTime={session.recording.recordingTime}
              micLevel={session.recording.micLevel}
              pendingSegments={session.recording.pendingSegments}
              encounterSetting={encounter.setting}
              onEncounterSettingChange={encounter.setSetting}
              noteFormat={encounter.noteFormat}
              onNoteFormatChange={encounter.setNoteFormat}
              specialty={encounter.specialty}
              onSpecialtyChange={encounter.setSpecialty}
              encounterSettingLocked={session.navigation.visitedSteps.has(STEPS.DIAGNOSIS)}
              textModeChosen={session.recording.textMode}
              onTextModeChange={session.recording.setTextMode}
              clarifyingQuestions={session.clarifyingQuestions.questions}
              clarifyingQuestionsLoading={session.clarifyingQuestions.loading}
              clarifyingQuestionsEnabled={session.clarifyingQuestions.enabled}
              contradictions={session.clarifyingQuestions.contradictions}
              onToggleContradictionResolved={
                session.clarifyingQuestions.toggleContradictionResolved
              }
              showTestEncounters={isGlobalAdmin}
              testEncounters={testEncounters}
              testEncountersLoading={testEncountersLoading}
              testEncountersError={testEncountersError}
              onSelectTestEncounter={handleSelectTestEncounter}
              onSaveTestEncounter={handleSaveTestEncounter}
              onDeleteTestEncounter={handleDeleteTestEncounter}
              onPatientRecordLoaded={session.patientContext.set}
              importedPatient={importedPatient}
              patientRecordUnavailable={session.patientContext.recordUnavailable}
              hideImportRecord={isSimulationMode}
              // ⚠️ ON FOR EVERYONE, KIJABE BETA INCLUDED, and that is a deliberate decision
              // rather than an oversight. Read this before switching it off again.
              //
              // Kijabe beta and admin simulation run with disableSave, so they never write a
              // chartmind/{sessionId} document — no transcript, no diagnoses, no chart. That
              // promise is untouched here: the relay is a DIFFERENT document, and nothing
              // about a phone recording creates a session doc.
              //
              // What it does mean is that a transcript briefly crosses a server for users
              // whose encounters are otherwise never written down, because text cannot reach
              // a second device any other way short of WebRTC. So the relay is built to be
              // transient rather than merely short-lived: deleted the moment the desktop has
              // the final text, deleted on cancel, deleted when this tab goes away, hard
              // expiry two hours out that no client can extend, get-only by one uid, and
              // never listable. It is a strictly smaller and shorter-lived artifact than the
              // session document every non-beta user already gets.
              //
              // Hidden on a phone, because there is no second device to hand the microphone
              // to: this IS the phone, and its own mic button is right there. Offering a QR
              // code to the camera of the screen showing it was the one arrangement that
              // could not work.
              showRecordOnPhone={!isPhone}
              phoneRecording={session.recording.phone.isPhoneRecording}
              // Closes the moment the phone says hello. The acknowledgement is an inline
              // success alert on the Record step, not a modal the clinician has to dismiss.
              phonePairing={
                session.recording.phone.isPairing || session.recording.phone.starting
              }
              phoneConnected={session.recording.phone.isPhoneConnected}
              phonePairingId={session.recording.phone.pairingId}
              phoneGrant={session.recording.phone.grant}
              phoneGrantUnavailable={session.recording.phone.grantUnavailable}
              phonePairingStarting={session.recording.phone.starting}
              phoneError={session.recording.phone.error || ""}
              phoneClaimedAt={session.recording.phone.phoneClaimedAt}
              onRecordOnPhone={handleRecordOnPhone}
              onCancelPhonePairing={session.recording.phone.cancel}
              onStopPhoneRecording={session.recording.phone.requestStop}
            />
          )}

          {session.navigation.currentStep === STEPS.DIAGNOSIS && (
            <DiagnosisStep
              transcript={encounter.sourceText}
              requireClinicianImpression={!eventsNote}
              ddxData={session.diagnosis.ddxData}
              loading={session.diagnosis.loading}
              error={session.diagnosis.error}
              hasDiagnoses={session.diagnosis.hasDiagnoses}
              retry={session.diagnosis.retry}
              selectedDiagnoses={session.diagnosis.selectedDiagnoses}
              onSelectionChange={session.diagnosis.onSelectionChange}
              onPrefetch={session.diagnosis.onPrefetch}
              disabledDiagnosisIds={session.diagnosis.disabledDiagnosisIds}
              onToggleDiagnosisDisabled={
                session.diagnosis.onToggleDiagnosisDisabled
              }
              customDiagnoses={session.diagnosis.customDiagnoses}
              onAddCustomDiagnosis={session.diagnosis.onAddCustomDiagnosis}
              clinicianImpression={session.diagnosis.clinicianImpression}
              onRecordClinicianImpression={
                session.diagnosis.onRecordClinicianImpression
              }
              clinicalReferences={session.diagnosis.clinicalReferences}
              embeddedDocuments={session.diagnosis.embeddedDocuments}
            />
          )}

          {pearlsAvailable && session.navigation.currentStep === STEPS.PEARLS && (
            <PearlsStep
              subject={session.pearls.subject}
              pearls={session.pearls.pearls}
              loading={session.pearls.loading}
              error={session.pearls.error}
              retry={session.pearls.retry}
            />
          )}

          {!CM0427_MODE && session.navigation.currentStep === STEPS.DIAGNOSTIC_PLAN && (
            <PlanStep
              selectedDiagnoses={session.diagnosis.selectedDiagnoses}
              planData={session.diagnosticPlan.planData}
              loading={session.diagnosticPlan.loading}
              error={session.diagnosticPlan.error}
              hasTests={session.diagnosticPlan.hasTests}
              retry={() =>
                session.diagnosticPlan.retry(
                  session.recording.transcript,
                  session.diagnosis.selectedDiagnoses,
                )
              }
              disabledTestIds={session.diagnosticPlan.disabledTestIds}
              onToggleTestDisabled={session.diagnosticPlan.onToggleTestDisabled}
              onAddTest={session.diagnosticPlan.addTest}
              testResults={session.diagnosticPlan.testResults}
              onUpdateTestResult={session.diagnosticPlan.onUpdateTestResult}
            />
          )}

          {!CM0427_MODE && session.navigation.currentStep === STEPS.CONFIRM_DIAGNOSIS && (
            <ConfirmDiagnosisStep
              transcript={session.recording.transcript}
              testResults={session.diagnosticPlan.testResults}
              originalDdxData={session.confirmDiagnosis.originalDdxData}
              confirmedData={session.confirmDiagnosis.confirmedData}
              loading={session.confirmDiagnosis.loading}
              error={session.confirmDiagnosis.error}
              hasConfirmedDiagnoses={
                session.confirmDiagnosis.hasConfirmedDiagnoses
              }
              retry={session.confirmDiagnosis.retry}
              selectedConfirmedDiagnoses={
                session.confirmDiagnosis.selectedConfirmedDiagnoses
              }
              onSelectionChange={session.confirmDiagnosis.onSelectionChange}
            />
          )}

          {session.navigation.currentStep === STEPS.TREATMENT && (
            <TreatmentPlanStep
              selectedDiagnoses={session.diagnosis.selectedDiagnoses}
              planData={session.treatment.planData}
              loading={session.treatment.loading}
              error={session.treatment.error}
              hasTreatments={session.treatment.hasTreatments}
              retry={() =>
                session.treatment.retry(
                  encounter.planSourceText,
                  session.diagnosis.selectedDiagnoses,
                  session.diagnosticPlan.planData,
                )
              }
              disabledTreatmentIds={session.treatment.disabledTreatmentIds}
              onToggleTreatmentDisabled={
                session.treatment.onToggleTreatmentDisabled
              }
              selectedTreatments={session.treatment.selectedTreatments}
              onToggleTreatmentSelected={
                session.treatment.onToggleTreatmentSelected
              }
              customTreatments={session.treatment.customTreatments}
              onAddCustomTreatment={session.treatment.onAddCustomTreatment}
              clinicalReferences={session.treatment.clinicalReferences}
              embeddedDocuments={session.treatment.embeddedDocuments}
              heading={
                eventsNote
                  ? {
                      title: "Plan",
                      subtitle: "Check the tests and treatments for this event.",
                    }
                  : inpatient
                    ? {
                        title: "Daily Plan",
                        subtitle:
                          "Check what you will do today. Use the pencil to add a note; checked items without one are listed as pending.",
                      }
                    : undefined
              }
              onEditTreatment={inpatient && !eventsNote ? openDailyPlanNote : undefined}
              editTooltip="Add a note"
              renderTreatmentNote={
                inpatient && !eventsNote
                  ? (treatment) => {
                      const comment = (encounter.dailyPlanComments[treatment.id] || "").trim();
                      const reassuring = Boolean(encounter.dailyPlanReassuring[treatment.id]);
                      if (!comment && !reassuring) return null;
                      // Same look as a result on the Tests step.
                      return (
                        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                          >
                            Note:
                          </Typography>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                            {[reassuring ? "Reassuring" : "", comment].filter(Boolean).join(" · ")}
                          </Typography>
                        </Box>
                      );
                    }
                  : undefined
              }
            />
          )}

          {eventsNote && session.navigation.currentStep === STEPS.EVENTS && (
            <EventsStep
              events={encounter.events}
              onAddEvent={encounter.addEvent}
              onUpdateEvent={encounter.updateEvent}
              onRemoveEvent={encounter.removeEvent}
              // The Event step is where an Events note starts, so the setup rows are here too.
              setup={{
                specialty: encounter.specialty,
                onSpecialtyChange: encounter.setSpecialty,
                encounterSetting: encounter.setting,
                onEncounterSettingChange: encounter.setSetting,
                noteFormat: encounter.noteFormat,
                onNoteFormatChange: encounter.setNoteFormat,
                disabled: session.navigation.visitedSteps.has(STEPS.DIAGNOSIS),
              }}
            />
          )}

          {session.navigation.currentStep === STEPS.DISCHARGE && (
            <DischargeStep
              transcript={session.recording.transcript}
              selectedDiagnoses={session.diagnosis.selectedDiagnoses}
              selectedTreatments={
                [
                  ...(session.treatment.planData?.treatments || []),
                  ...(session.treatment.customTreatments || []),
                ].filter(
                  (t) =>
                    session.treatment.selectedTreatments.has(t.id) &&
                    !session.treatment.disabledTreatmentIds.has(t.id),
                )
              }
              autoGenerate={true}
              instructions={session.discharge.instructions}
              includedInstructionIds={session.discharge.includedInstructionIds}
              loading={session.discharge.loading}
              error={session.discharge.error}
              isGenerating={session.discharge.isGenerating}
              hasBeenGenerated={session.discharge.hasBeenGenerated}
              generateDischargeInstructions={
                session.discharge.generateDischargeInstructions
              }
              regenerateDischargeInstructions={
                session.discharge.regenerateDischargeInstructions
              }
              toggleInstructionIncluded={
                session.discharge.toggleInstructionIncluded
              }
              updateInstructionProse={session.discharge.updateInstructionProse}
              clinicalReferences={session.discharge.clinicalReferences}
              embeddedDocuments={session.discharge.embeddedDocuments}
              patientContext={session.patientContext.payload}
              importedPatient={importedPatient}
              onSendDischarge={handleSendDischarge}
              onCreateDischargeHandoff={handleCreateDischargeHandoff}
              getPatientDischarge={getPatientDischarge}
            />
          )}

          {session.navigation.currentStep === STEPS.CHART && (
            <ChartStep
              noteSections={session.chart.noteSections}
              template={session.chart.template}
              feedbackRating={session.chart.feedback?.rating ?? null}
              feedbackRemarks={session.chart.feedback?.remarks ?? ""}
              loading={session.chart.loading}
              error={session.chart.error}
              onUpdateSection={session.chart.updateSection}
              onFeedbackRatingChange={session.chart.updateFeedbackRating}
              onFeedbackRemarksChange={session.chart.updateFeedbackRemarks}
              onCopyToClipboard={session.chart.onCopyToClipboard}
              onExportToPdf={session.chart.onExportToPdf}
              onRegenerate={session.chart.onRegenerate}
              isStale={session.chart.isStale}
              generatedWithoutDiagnoses={session.chart.generatedWithoutDiagnoses}
              clinicalReferences={session.chart.clinicalReferences}
              embeddedDocuments={session.chart.embeddedDocuments}
              // Dictating an addendum uses the encounter's own language, not the browser's.
              language={session.recording.language}
              phoneDictation={session.recording.phoneDictation}
              partnerName={partnerImport?.clientName}
              onSendToPartner={partnerImport ? handleSendToPartner : undefined}
            />
          )}
      </Box>

      {/* Footer moved into StepperPageShell's footer slot above. */}

      {(isGlobalAdmin || isOrgAdmin) ? (
        <Alert
          severity="warning"
          variant="standard"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/dashboard/prompts")}
            >
              Manage Prompts
            </Button>
          }
          sx={{
            flexShrink: 0,
            borderRadius: 0,
            fontSize: "0.75rem",
            py: 0.25,
            alignItems: "center",
            "& .MuiAlert-icon": { fontSize: "1rem" },
          }}
        >
          Simulation mode for prompt development. Not intended for medical use.
        </Alert>
      ) : null}

      <DailyPlanNoteDialog
        open={dailyPlanNoteOpen}
        item={dailyPlanNoteItem}
        comment={dailyPlanNoteItem ? encounter.dailyPlanComments[dailyPlanNoteItem.id] || "" : ""}
        reassuring={Boolean(dailyPlanNoteItem && encounter.dailyPlanReassuring[dailyPlanNoteItem.id])}
        onClose={() => setDailyPlanNoteOpen(false)}
        onExited={() => setDailyPlanNoteItem(null)}
        onSave={(itemId, { comment, reassuring }) => {
          encounter.setDailyPlanComment(itemId, comment);
          encounter.setDailyPlanReassuring(itemId, reassuring);
        }}
      />

      <ConfirmDialog
        open={confirmPhoneRecordingOpen}
        onClose={() => setConfirmPhoneRecordingOpen(false)}
        title="Record on your phone?"
        message="This starts a new encounter, so the transcript, clarifying questions, diagnoses, treatment plan and chart on this screen will be cleared."
        confirmLabel="Continue"
        confirmColor="error"
        onConfirm={handleConfirmPhoneRecording}
      />

      <ConfirmDialog
        open={confirmNewEncounterOpen}
        onClose={() => setConfirmNewEncounterOpen(false)}
        title="Start a new encounter?"
        message={
          isEphemeralSession
            ? "Your current encounter is not saved. Starting a new one will permanently discard the transcript, diagnoses, treatment plan, and chart."
            : "Starting a new encounter will close the one you have open. (Your saved sessions are still available under My Charts.)"
        }
        confirmLabel="Start new"
        confirmColor={isEphemeralSession ? "error" : "primary"}
        onConfirm={handleConfirmNewEncounter}
      />
    </StepperPageShell>
  );
};

export default ChartMindPage;
